export type ShowcasePdfSection = {
  heading: string;
  body: string[];
};

type PdfInput = {
  title: string;
  subtitle: string;
  sections: ShowcasePdfSection[];
};

type PdfLine = {
  text: string;
  x: number;
  y: number;
  font: "F1" | "F2";
  size: number;
  color: string;
};

const PAGE_WIDTH = 595.28;
const PAGE_HEIGHT = 841.89;
const MARGIN_X = 54;
const MARGIN_TOP = 56;
const MARGIN_BOTTOM = 58;
const BODY_SIZE = 10.5;
const LINE_HEIGHT = 15;
const HEADING_SIZE = 12.5;

function byteLength(value: string): number {
  return new TextEncoder().encode(value).length;
}

function pdfHex(value: string): string {
  let hex = "FEFF";
  for (let index = 0; index < value.length; index += 1) {
    hex += value.charCodeAt(index).toString(16).padStart(4, "0").toUpperCase();
  }
  return `<${hex}>`;
}

function wrapText(value: string, fontSize: number): string[] {
  const maxChars = Math.max(
    32,
    Math.floor((PAGE_WIDTH - MARGIN_X * 2) / (fontSize * 0.53)),
  );
  const words = value.replace(/\s+/g, " ").trim().split(" ").filter(Boolean);
  if (words.length === 0) {
    return [""];
  }

  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    if (word.length > maxChars) {
      if (current) {
        lines.push(current);
        current = "";
      }
      for (let index = 0; index < word.length; index += maxChars) {
        lines.push(word.slice(index, index + maxChars));
      }
      continue;
    }
    const next = current ? `${current} ${word}` : word;
    if (next.length > maxChars) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  }
  if (current) {
    lines.push(current);
  }
  return lines;
}

function addLine(
  pages: PdfLine[][],
  line: Omit<PdfLine, "y">,
  state: { y: number },
  gapAfter = 0,
) {
  if (pages.length === 0) {
    pages.push([]);
  }
  if (state.y < MARGIN_BOTTOM + LINE_HEIGHT) {
    pages.push([]);
    state.y = PAGE_HEIGHT - MARGIN_TOP;
  }
  pages[pages.length - 1].push({ ...line, y: state.y });
  state.y -= LINE_HEIGHT + gapAfter;
}

function addWrappedLines(
  pages: PdfLine[][],
  text: string,
  options: Omit<PdfLine, "text" | "y">,
  state: { y: number },
  gapAfter = 0,
) {
  const wrapped = wrapText(text, options.size);
  wrapped.forEach((line, index) => {
    addLine(
      pages,
      { ...options, text: line },
      state,
      index === wrapped.length - 1 ? gapAfter : 0,
    );
  });
}

function layoutPdf(input: PdfInput): PdfLine[][] {
  const pages: PdfLine[][] = [[]];
  const state = { y: PAGE_HEIGHT - MARGIN_TOP };

  addWrappedLines(
    pages,
    input.title,
    {
      x: MARGIN_X,
      font: "F2",
      size: 16,
      color: "0.02 0.10 0.06",
    },
    state,
    4,
  );
  addWrappedLines(
    pages,
    input.subtitle,
    {
      x: MARGIN_X,
      font: "F1",
      size: 10,
      color: "0.25 0.38 0.31",
    },
    state,
    12,
  );

  for (const section of input.sections) {
    addWrappedLines(
      pages,
      section.heading,
      {
        x: MARGIN_X,
        font: "F2",
        size: HEADING_SIZE,
        color: "0.03 0.18 0.09",
      },
      state,
      2,
    );

    for (const bodyLine of section.body) {
      addWrappedLines(
        pages,
        bodyLine,
        {
          x: MARGIN_X + 8,
          font: "F1",
          size: BODY_SIZE,
          color: "0.12 0.16 0.21",
        },
        state,
        2,
      );
    }
    state.y -= 6;
  }

  return pages;
}

function watermarkCommands(): string {
  const watermark = pdfHex("darsi.ma preview");
  const angle = -35 * (Math.PI / 180);
  const cos = Math.cos(angle).toFixed(4);
  const sin = Math.sin(angle).toFixed(4);
  const commands = [
    "q",
    "/GS1 gs",
    "0.10 0.45 0.26 rg",
    "/F2 34 Tf",
    "BT",
  ];

  for (let y = 10; y < PAGE_HEIGHT + 210; y += 170) {
    for (let x = -170; x < PAGE_WIDTH + 240; x += 280) {
      commands.push(`${cos} ${sin} ${(-Number(sin)).toFixed(4)} ${cos} ${x} ${y} Tm ${watermark} Tj`);
    }
  }

  commands.push("ET", "Q");
  return commands.join("\n");
}

function pageContent(lines: PdfLine[], pageNumber: number, pageCount: number): string {
  const body = lines
    .map(
      (line) =>
        `BT /${line.font} ${line.size} Tf ${line.color} rg 1 0 0 1 ${line.x.toFixed(2)} ${line.y.toFixed(2)} Tm ${pdfHex(line.text)} Tj ET`,
    )
    .join("\n");
  const footer = `BT /F1 8 Tf 0.35 0.45 0.40 rg 1 0 0 1 ${MARGIN_X.toFixed(2)} 32 Tm ${pdfHex(`darsi.ma preview - page ${pageNumber} of ${pageCount}`)} Tj ET`;
  return [watermarkCommands(), body, footer].join("\n");
}

function buildPdf(input: PdfInput): string {
  const pages = layoutPdf(input);
  const fontRegularObj = 3 + pages.length * 2;
  const fontBoldObj = fontRegularObj + 1;
  const pageKids = pages
    .map((_, index) => `${3 + index * 2} 0 R`)
    .join(" ");

  const objects: string[] = [
    "1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n",
    `2 0 obj\n<< /Type /Pages /Kids [${pageKids}] /Count ${pages.length} >>\nendobj\n`,
  ];

  pages.forEach((lines, index) => {
    const pageObj = 3 + index * 2;
    const contentObj = pageObj + 1;
    const stream = pageContent(lines, index + 1, pages.length);
    objects.push(
      `${pageObj} 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_WIDTH} ${PAGE_HEIGHT}] /Resources << /Font << /F1 ${fontRegularObj} 0 R /F2 ${fontBoldObj} 0 R >> /ExtGState << /GS1 << /Type /ExtGState /ca 0.12 /CA 0.12 >> >> >> /Contents ${contentObj} 0 R >>\nendobj\n`,
      `${contentObj} 0 obj\n<< /Length ${byteLength(stream)} >>\nstream\n${stream}\nendstream\nendobj\n`,
    );
  });

  objects.push(
    `${fontRegularObj} 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>\nendobj\n`,
    `${fontBoldObj} 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>\nendobj\n`,
  );

  let pdf = "%PDF-1.4\n";
  const offsets = [0];
  for (const object of objects) {
    offsets.push(byteLength(pdf));
    pdf += object;
  }

  const xrefOffset = byteLength(pdf);
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  for (const offset of offsets.slice(1)) {
    pdf += `${offset.toString().padStart(10, "0")} 00000 n \n`;
  }
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;
  return pdf;
}

function safeFileName(value: string): string {
  const cleaned = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 72);
  return cleaned || "darsi-preview";
}

export function downloadWatermarkedShowcasePdf(input: PdfInput) {
  const pdf = buildPdf(input);
  const blob = new Blob([pdf], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${safeFileName(input.title)}-watermarked-preview.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
